import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, API_URL } from './api';

export interface EntryRow {
  id: string;
  workDate: string;
  begin: string;
  end: string | null;
  durationSeconds: number | null;
  description: string | null;
  billable: boolean;
  exported: boolean;
  employee: { id: string; displayName: string };
  project: { id: string; name: string; customer: { id: string; name: string } };
  activity: { id: string; name: string };
  tags: { id: string; name: string; color: string | null }[];
}

export interface EntryPage {
  rows: EntryRow[];
  total: number;
  page: number;
  pageSize: number;
  /** Across the whole filter, not just this page. */
  totalSeconds: number;
}

export interface TotalRow {
  key: string;
  label: string;
  seconds: number;
}

export type Dimension = 'employee' | 'customer' | 'project' | 'activity' | 'month';

export interface Filters {
  from?: string;
  to?: string;
  employeeIds?: string[];
  customerIds?: string[];
  projectIds?: string[];
  activityIds?: string[];
  billable?: boolean;
  exported?: boolean;
  search?: string;
}

/** Filters as a query string. Empty values are omitted, not sent as blanks. */
export function toQuery(filters: Filters, extra: Record<string, string | number> = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries({ ...filters, ...extra })) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  return params.toString();
}

export const useEntries = (filters: Filters, page: number, pageSize = 50) =>
  useQuery({
    queryKey: ['reporting', 'entries', filters, page, pageSize],
    queryFn: () => api.get<EntryPage>(`/reporting/entries?${toQuery(filters, { page, pageSize })}`),
  });

export const useTotals = (by: Dimension, filters: Filters, enabled = true) =>
  useQuery({
    queryKey: ['reporting', 'totals', by, filters],
    queryFn: () => api.get<TotalRow[]>(`/reporting/totals?${toQuery(filters, { by })}`),
    enabled,
  });

/**
 * The export is a link, not a fetch.
 *
 * The browser saves the file itself, with the filename the API sets; fetching it
 * into memory first would mean re-implementing the download for no gain.
 */
export const exportCsvUrl = (filters: Filters): string =>
  `${API_URL}/reporting/export.csv?${toQuery(filters)}`;

export interface ExportSummary {
  entries: number;
  totalSeconds: number;
  alreadyExported: number;
  notYetExported: number;
}

/** What an export would cover, asked before anything is downloaded or marked. */
export const useExportSummary = (filters: Filters) =>
  useQuery({
    queryKey: ['reporting', 'export-summary', filters],
    queryFn: () => api.get<ExportSummary>(`/reporting/export-summary?${toQuery(filters)}`),
  });

/**
 * Mark everything the filter covers as exported.
 *
 * Sends the filter, not a list of ids, so the server marks provably the same set
 * the CSV was built from rather than a list the browser assembled separately.
 */
export const useMarkExported = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filters: Filters) =>
      api.post<{ marked: number }>('/reporting/mark-exported', filters),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reporting'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
};

/** Clear the exported mark on everything the filter covers. Administrators only. */
export const useUnmarkExported = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filters: Filters) =>
      api.post<{ cleared: number; onInvoice: number }>('/reporting/unmark-exported', filters),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reporting'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
};

export interface BulkPatch {
  ids: string[];
  projectId?: string;
  activityId?: string;
  billable?: boolean;
  tagIds?: string[];
  replaceTags?: boolean;
}

/**
 * Change several entries at once.
 *
 * All or nothing: the server checks every entry before writing any, and refuses
 * the whole selection naming the first blocker. A half-applied bulk edit leaves
 * somebody comparing a filter against the database to work out which half landed.
 */
export const useBulkUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: BulkPatch) =>
      api.post<{ updated: number }>('/timesheet/bulk-update', patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reporting'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
};

export interface SavedFilter {
  id: string;
  name: string;
  query: string;
}

export const useSavedFilters = (screen: string) =>
  useQuery({
    queryKey: ['reporting', 'filters', screen],
    queryFn: () => api.get<SavedFilter[]>(`/reporting/filters?screen=${screen}`),
  });

export const useSaveFilter = (screen: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; query: string }) =>
      api.post<SavedFilter>('/reporting/filters', { screen, ...args }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reporting', 'filters', screen] }),
  });
};

export const useDeleteFilter = (screen: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/reporting/filters/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reporting', 'filters', screen] }),
  });
};

/** Rebuild filters from a saved query string. The inverse of toQuery. */
export function fromQuery(query: string): Filters {
  const p = new URLSearchParams(query);
  const list = (k: string) => {
    const v = p.get(k);
    return v ? v.split(',').filter(Boolean) : undefined;
  };
  const bool = (k: string) => {
    const v = p.get(k);
    return v === null ? undefined : v === 'true';
  };
  return {
    from: p.get('from') ?? undefined,
    to: p.get('to') ?? undefined,
    employeeIds: list('employeeIds'),
    customerIds: list('customerIds'),
    projectIds: list('projectIds'),
    activityIds: list('activityIds'),
    billable: bool('billable'),
    exported: bool('exported'),
    search: p.get('search') ?? undefined,
  };
}

/** Customer, then its projects, then the activities on them. Biggest first. */
export interface BreakdownActivity { id: string; name: string; seconds: number }
export interface BreakdownProject {
  id: string;
  name: string;
  seconds: number;
  activities: BreakdownActivity[];
}
export interface BreakdownCustomer {
  id: string;
  name: string;
  seconds: number;
  projects: BreakdownProject[];
}

export const useBreakdown = (filters: Filters, enabled: boolean) =>
  useQuery({
    queryKey: ['reporting', 'breakdown', filters],
    queryFn: () => api.get<BreakdownCustomer[]>(`/reporting/breakdown?${toQuery(filters)}`),
    enabled,
  });
